import { Box, Typography } from '@mui/material';
import {
  useAudioVideo,
  useLocalVideo,
  useLogger,
  useMeetingManager,
  useToggleLocalMute,
  useVideoInputs,
} from 'amazon-chime-sdk-component-library-react';
import { useEffect, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import ControlBarButton from './controlBar/button';
import MicrophoneIcon from './icons/microphoneIcon';
import VideoCameraIcon from './icons/videoCameraIcon';
import VideoTile from './videoTile';

import { useMeetingContext } from '../../contexts/meeting/meetingContext';

function PreviewUserVideo() {
  const { muted, toggleMute } = useToggleLocalMute();
  const { isVideoEnabled, setIsVideoEnabled } = useLocalVideo();
  const { isVoiceOnlyCall } = useMeetingContext();

  const logger = useLogger();
  const audioVideo = useAudioVideo();
  const { selectedDevice } = useVideoInputs();
  const videoEl = useRef<HTMLVideoElement>(null);
  const meetingManager = useMeetingManager();

  useEffect(() => {
    const videoElement = videoEl.current;
    return () => {
      if (videoElement) {
        audioVideo?.stopVideoPreviewForVideoInput(videoElement);
        audioVideo?.stopVideoInput();
        setIsVideoEnabled(false);
      }
    };
  }, [audioVideo, setIsVideoEnabled]);

  useEffect(() => {
    async function startPreview(): Promise<void> {
      if (!audioVideo || !selectedDevice || !videoEl.current) {
        return;
      }

      try {
        await meetingManager.startVideoInputDevice(selectedDevice);
        audioVideo.startVideoPreviewForVideoInput(videoEl.current);
        setIsVideoEnabled(true);
      } catch (error) {
        logger.error('Failed to start video preview');
      }
    }

    startPreview();
  }, [audioVideo, selectedDevice, meetingManager, logger, setIsVideoEnabled]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: isVideoEnabled ? 'flex' : 'none',
        }}
      >
        <VideoTile ref={videoEl} hidden={!isVideoEnabled} />
      </Box>
      <Box
        sx={{
          display: isVideoEnabled ? 'none' : 'flex',
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#333',
        }}
      >
        <Typography
          sx={{ color: (theme) => theme.palette.neutralBlackBg[100] }}
          fontSize={18}
        >
          {!isVoiceOnlyCall && (
            <FormattedMessage defaultMessage='Camera is turned off' />
          )}
          {isVoiceOnlyCall && (
            <>
              {muted && (
                <FormattedMessage defaultMessage='Microphone is turned off' />
              )}
              {!muted && (
                <FormattedMessage defaultMessage='Microphone is turned on' />
              )}
            </>
          )}
        </Typography>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          bottom: '20px',
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          gap: '20px',
        }}
      >
        <ControlBarButton
          icon={<MicrophoneIcon muted={muted} />}
          active={!muted}
          onClick={toggleMute}
        />
        {!isVoiceOnlyCall && (
          <ControlBarButton
            icon={<VideoCameraIcon videoEnabled={isVideoEnabled} />}
            active={isVideoEnabled}
            onClick={() => {
              setIsVideoEnabled(!isVideoEnabled);
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export default PreviewUserVideo;
